'use client';

import { CatRadioField } from '@blocks-react/components/RadioField';
import { useFormContext } from 'react-hook-form';
import styles from './styles.module.scss';
import { CatTextLink } from '@blocks-react/components/TextLink';
import { CatIconPhone } from '@blocks-react/components/Icons/Phone';
import { useEffect } from 'react';
import cx from 'classnames';
import { useExpander } from '@cat-ecom/pcc-components/src/hooks/useExpander';
import {
  Conditional,
  AssociatedDealerSearchResultExpander as ExpanderButton
} from '@cat-ecom/pcc-components';
import { CustomerStore } from 'components/associated-dealers/types';

interface Props {
  name: string;
  store: CustomerStore;
  isFirstValue: boolean;
  isLastValue: boolean;
  disabled: boolean;
}

export const SearchResult = ({
  name,
  store,
  isFirstValue,
  isLastValue,
  disabled
}: Props) => {
  const { isExpanded, toggle } = useExpander();
  const { setValue, resetField, watch } = useFormContext();
  const handleExpander = () => toggle(!isExpanded);

  const { line1, line2, line3 } = store.address;
  const line = line1 || line2 || line3;
  const address = `${store?.city}, ${store?.zipcode}`;
  /**
   * The consumer of the component will provide that isFirstValue flag,
   * we will use that flag to set the provided store prop as the field value
   * and to expand the accordion
   */
  useEffect(() => {
    if (isFirstValue) {
      setValue(name, store?.customerNumber);
      handleExpander();
    }

    return () => {
      resetField(name);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: CustomEvent) => {
    setValue(name, e.detail.value);
  };

  const selectedValue = watch(name);

  return (
    <div
      className={styles[`search-result-container${isLastValue ? '-last' : ''}`]}
    >
      <div className="d-flex flex-column justify-content-between py-3">
        <div className="d-flex justify-content-between align-items-cente">
          <CatRadioField
            name={name}
            data-testid="associated-dealer-radio"
            id={store?.customerNumber}
            value={store?.customerNumber}
            checked={selectedValue === store?.customerNumber}
            onBlRadioChange={handleChange}
            disabled={disabled}
          >
            <p
              className={cx(
                styles['search-result-radio-field-text'],
                `cat-u-theme-typography-body-sm mb-0`
              )}
            >
              {store?.customerNumber}
            </p>
          </CatRadioField>
          <ExpanderButton
            handleExpander={handleExpander}
            isExpanded={isExpanded}
            disabled={disabled}
          />
        </div>
        <div className="my-0">
          <p className="cat-u-theme-typography-label-lg mt-1 mb-0">
            {store?.name}
          </p>
          <p className="cat-u-theme-typography-body my-0">{address}</p>
        </div>
        <Conditional test={isExpanded}>
          {line && (
            <div className="cat-u-theme-typography-body my-0">
              {line1 && <p className="mb-0">{line1}</p>}
              {line2 && <p className="mb-0">{line2}</p>}
              {line3 && <p>{line3}</p>}
            </div>
          )}
          <div className="d-flex flex-column pb-3">
            <CatTextLink
              href={`tel:${store?.phone}`}
              className={`d-flex align-items-center data-cs-mask`}
            >
              <div className="d-flex align-items-center me-2">
                <CatIconPhone />
              </div>
              <span className="cat-u-theme-typography-label data-cs-mask">
                {store?.phone}
              </span>
            </CatTextLink>
          </div>
        </Conditional>
      </div>
    </div>
  );
};
